import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { MapPin, Search as SearchIcon, Loader2, PawPrint, Star } from "lucide-react";
import { api } from "../lib/api";
import { track } from "../lib/analytics";
import { getCategory } from "../lib/categories";
import { placeUrl } from "../lib/placeUrl";

/**
 * LocationAutocomplete
 * Controlled input with a Google-Places-backed dropdown of city/zip suggestions.
 * Renders the dropdown as a body portal to avoid parent stacking-context issues.
 */
const LocationAutocomplete = ({
  value,
  onChange,
  onPick,
  placeholder = "Search place name, city, or ZIP",
  size = "lg",
  className = "",
  testId = "location-autocomplete",
  showButton = true,
  buttonLabel = "Find parks",
  autoFocus = false,
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  // True when the LAST completed fetch returned zero combined results
  // for a 3+ char query — drives the "No results found" empty state.
  // Reset on every new keystroke (in the effect above) so we never
  // flash "No results" while a new query is in flight.
  const [noResults, setNoResults] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [coords, setCoords] = useState(null); // {top, left, width}
  const inputRef = useRef(null);
  const inputWrapRef = useRef(null);
  const debounceRef = useRef(null);
  // AbortController for the in-flight autocomplete HTTP pair. When the
  // user types another keystroke we abort BOTH parallel requests so we
  // never paint stale predictions over what the next keystroke fetches.
  // (clearing the debounce timer above only cancels the un-fired call —
  // a request that already left the wire keeps running otherwise.)
  const abortRef = useRef(null);
  const lastFetched = useRef("");
  const skipNextFetch = useRef(false);
  // Client-side cache: query (lowercased) → suggestions[]. Stores the merged
  // (place + location) result so prefix toggles feel instant.
  const cacheRef = useRef(new Map());
  const CACHE_MAX = 50;
  // Dedupe GA4 `autocomplete_no_results` event firings — fire once per
  // unique empty query per component mount so we don't double-count
  // when a user re-types the same dead-end query or when the in-session
  // cache returns the cached empty result.
  const trackedEmptyRef = useRef(new Set());

  const fireEmptyQueryEvent = (query) => {
    const key = query.toLowerCase();
    if (trackedEmptyRef.current.has(key)) return;
    trackedEmptyRef.current.add(key);
    // GA4 caps `query` to 100 chars — trim so we never get rejected
    // events. Send the raw input string so SEO content-gap analysis
    // can spot patterns ("downtown brooklyn", "south austin", etc.).
    track("autocomplete_no_results", {
      query: query.slice(0, 100),
      query_length: query.length,
      surface: testId,
    });
  };

  // Position dropdown beneath the input
  const recompute = () => {
    if (!inputWrapRef.current) return;
    const r = inputWrapRef.current.getBoundingClientRect();
    setCoords({ top: r.bottom + 8, left: r.left, width: r.width });
  };

  useLayoutEffect(() => {
    if (!open) return;
    recompute();
    const handler = () => recompute();
    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, true);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler, true);
    };
  }, [open, suggestions.length]);

  // Debounced fetch
  useEffect(() => {
    // Cancel ANY in-flight request from the previous keystroke. Without
    // this, a slow response could land AFTER the user kept typing and
    // overwrite the correct, in-progress dropdown.
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = (value || "").trim();
    // Skip the fetch immediately following a pick/submit so the dropdown
    // does not re-open after `onChange(s.description)` updates the value.
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      lastFetched.current = trimmed;
      setSuggestions([]);
      setLoading(false);
      setNoResults(false);
      return;
    }
    // Min 3 chars before hitting Google. Saves 2 keystrokes worth of
    // autocomplete calls per query — at $2.83/1000 these add up at scale.
    // The dropdown stays closed during 1-2 char input so users don't
    // see suggestions appearing and vanishing mid-typing.
    if (trimmed.length < 3) {
      setSuggestions([]);
      setLoading(false);
      setNoResults(false);
      return;
    }
    // Numeric-only queries: previously suppressed entirely on the
    // frontend because neither name_search nor the (regions)-typed
    // Google autocomplete resolved them. As of 9/3/2026, the backend
    // `/api/cities/autocomplete` DOES handle bare 5-digit US ZIPs —
    // resolves via `parks.zip`, returns one prediction per (city,
    // state) in that ZIP. So we let 5-digit numerics through and
    // keep suppressing other digit strings (partial ZIPs, ZIP+4,
    // phone-number fragments) that still resolve to nothing on
    // both endpoints and would just flash "No results" in the
    // dropdown mid-typing.
    if (/^\d+$/.test(trimmed) && trimmed.length !== 5) {
      setSuggestions([]);
      setLoading(false);
      setNoResults(false);
      setOpen(false);
      return;
    }
    if (trimmed === lastFetched.current) return;

    // Instant cache hit — show last known predictions for this query without
    // a spinner or network round-trip. Background-refresh is intentionally
    // skipped: Google's region results are stable enough within a session.
    const cacheKey = trimmed.toLowerCase();
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      lastFetched.current = trimmed;
      setSuggestions(cached);
      setLoading(false);
      const empty = cached.length === 0;
      setNoResults(empty);
      if (empty) fireEmptyQueryEvent(trimmed);
      if (document.activeElement === inputRef.current) setOpen(true);
      setHighlight(-1);
      return;
    }

    setLoading(true);
    setNoResults(false);
    // Clear stale suggestions immediately so the dropdown shows ONLY the
    // spinner during the debounce/network wait — never a flash of old
    // results from a previous query.
    setSuggestions([]);
    // Open the dropdown right away (if input is focused) so the user
    // gets immediate "Searching…" feedback during the debounce + fetch
    // window — was previously only opened AFTER the fetch resolved,
    // which felt like nothing was happening for 400-800ms.
    if (document.activeElement === inputRef.current) setOpen(true);
    // 400ms debounce: long enough to absorb typical typing bursts
    // ("Tampa" is 5 keystrokes; if the user types at 4chars/sec we
    // fire once after the last keystroke), short enough that the
    // dropdown still feels responsive.
    debounceRef.current = setTimeout(async () => {
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      try {
        // Parallel fetch: place-name search (our own DB) + city
        // autocomplete (also our own DB, MongoDB-backed since the
        // Google Places Autocomplete integration was retired).
        // Whichever resolves first ships partial results; the second
        // updates when it lands. UX-wise this means typing "Deputy"
        // shows "Deputy Kotfila Memorial Dog Park" immediately even
        // before the city index has any region matches.
        const [placesResp, locsResp] = await Promise.allSettled([
          api.get("/places/name_search", { params: { q: trimmed, limit: 5 }, signal: ctrl.signal }),
          api.get("/cities/autocomplete", {
            params: { input: trimmed, limit: 5 },
            signal: ctrl.signal,
          }),
        ]);
        // If we got aborted mid-flight, both responses are rejected with
        // AbortError. Bail out without touching state — the next
        // keystroke is already handling things.
        if (ctrl.signal.aborted) return;

        const placeMatches =
          placesResp.status === "fulfilled"
            ? (placesResp.value.data?.results || []).map((p) => ({
                kind: "place",
                place_id: `place:${p.id}`,
                id: p.id,
                name: p.name,
                place_type: p.place_type,
                rating: p.rating,
                city: p.city,
                state: p.state,
                address: p.address,
                // Full source flag + slug bubble up here so placeUrl()
                // can build the canonical Fix #2 detail URL when the
                // dropdown row is picked.
                source: p.source,
                slug: p.slug,
                // Shape that matches the existing location-suggestion render:
                main_text: p.name,
                secondary_text: [p.city, p.state].filter(Boolean).join(", ") || p.address,
                description: p.name,
              }))
            : [];
        const locationPreds =
          locsResp.status === "fulfilled"
            ? (locsResp.value.data?.predictions || []).map((s) => {
                // The /places/autocomplete endpoint now also surfaces
                // manually-curated business listings (`source==="manual_add"`)
                // alongside Google's region predictions. Those need to
                // route to /park/<id> instead of filling the input, so
                // we re-shape them as `kind: "place"` here. Parsing
                // the id out of the `manual:<id>` place_id prefix the
                // backend assigns.
                if (s.source === "manual_add" && s.place_id?.startsWith("manual:")) {
                  const id = s.place_id.slice("manual:".length);
                  return {
                    kind: "place",
                    place_id: `place:${id}`,
                    id,
                    name: s.main_text,
                    main_text: s.main_text,
                    secondary_text: s.secondary_text,
                    description: s.description,
                    source: "manual_add",
                  };
                }
                return { kind: "location", ...s };
              })
            : [];

        // Dedupe: a manual entry can surface via BOTH /name_search and
        // /autocomplete (defense-in-depth — see comments on the
        // autocomplete handler). Keep the first occurrence per id so
        // the dropdown shows one row per business. `kind: "location"`
        // rows never carry an `id` so they pass through untouched.
        const seenIds = new Set();
        const dedupe = (arr) =>
          arr.filter((s) => {
            if (!s.id) return true;
            if (seenIds.has(s.id)) return false;
            seenIds.add(s.id);
            return true;
          });

        // Order: location/city predictions FIRST, then business name
        // matches. Users typing a city name expect "Tampa, FL" to
        // bubble to the top — individual businesses with the same
        // word in their name (Tampa Paws Pet Salon, Tampa Tap Room)
        // are useful but secondary signals.
        const merged = dedupe([...locationPreds, ...placeMatches]);
        lastFetched.current = trimmed;
        cacheRef.current.set(cacheKey, merged);
        if (cacheRef.current.size > CACHE_MAX) {
          const oldest = cacheRef.current.keys().next().value;
          cacheRef.current.delete(oldest);
        }
        setSuggestions(merged);
        const empty = merged.length === 0;
        setNoResults(empty);
        if (empty) fireEmptyQueryEvent(trimmed);
        if (document.activeElement === inputRef.current) {
          setOpen(true);
        }
        setHighlight(-1);
      } catch {
        // Network failure OR abort — either way, no harm. The next
        // keystroke (or the abort-triggered next render) handles UI.
        if (!ctrl.signal.aborted) {
          setSuggestions([]);
          setNoResults(false);
        }
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
        if (abortRef.current === ctrl) abortRef.current = null;
      }
    }, 400);
    return () => {
      clearTimeout(debounceRef.current);
      // Also abort on unmount/effect-rerun so we don't leak in-flight
      // requests if the component unmounts mid-fetch.
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, [value]);

  // Close on outside interaction (mouse + touch)
  useEffect(() => {
    const onDoc = (e) => {
      if (
        inputWrapRef.current?.contains(e.target) ||
        e.target.closest?.(`[data-pg-autocomplete="${testId}"]`)
      ) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, [testId]);

  const closeDropdown = () => {
    skipNextFetch.current = true;
    setOpen(false);
    setSuggestions([]);
    setLoading(false);
    setNoResults(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // Blur to dismiss mobile keyboard and stop focus-driven reopen.
    inputRef.current?.blur();
  };

  const choose = (s) => {
    closeDropdown();
    // Place pick → jump straight to the place detail page; the input
    // becomes a no-op (we don't want to mutate the location-search value).
    if (s.kind === "place") {
      navigate(placeUrl(s));
      return;
    }
    onChange?.(s.description);
    onPick?.(s.description, s);
  };

  const handleSubmit = () => {
    closeDropdown();
    onPick?.(value || "");
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (open && highlight >= 0 && suggestions[highlight]) {
        choose(suggestions[highlight]);
      } else {
        handleSubmit();
      }
    } else if (e.key === "Escape") {
      closeDropdown();
    }
  };

  const inputClasses =
    size === "lg"
      ? "w-full pl-12 pr-4 py-4 bg-white border border-brand-border-warm rounded-full text-base placeholder:text-brand-ink/40 focus:outline-none focus:ring-2 focus:ring-brand-forest focus:border-brand-forest transition-all"
      : "w-full pl-12 pr-24 py-3.5 bg-white border border-brand-border-warm rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-brand-forest focus:border-brand-forest";

  const dropdown =
    open && coords && (suggestions.length > 0 || loading || noResults) ? (
      <div
        data-pg-autocomplete={testId}
        data-testid={`${testId}-dropdown`}
        style={{
          position: "fixed",
          top: coords.top,
          left: coords.left,
          width: coords.width,
          zIndex: 9999,
        }}
        className="bg-white border border-brand-border-warm rounded-2xl shadow-warm-lg overflow-hidden animate-fade-in-up"
      >
        {loading && suggestions.length === 0 && (
          <div
            data-testid={`${testId}-loading`}
            className="px-4 py-3 text-sm text-brand-ink/60 flex items-center gap-2"
          >
            <Loader2 className="w-3.5 h-3.5 animate-spin text-brand-forest" /> Searching…
          </div>
        )}
        {!loading && noResults && suggestions.length === 0 && (
          <div
            data-testid={`${testId}-empty`}
            className="px-4 py-3 text-sm text-brand-ink/60"
          >
            No results found
          </div>
        )}
        {suggestions.map((s, i) => {
          const active = i === highlight;
          const isPlace = s.kind === "place";
          // For place suggestions, swap the leading icon for the
          // category's signature colored circle so users can tell at a
          // glance "this is a dog park" vs "this is a vet" without
          // reading the secondary text.
          const placeCat = isPlace ? getCategory(s.place_type || "park") : null;
          const Icon = placeCat ? placeCat.icon : null;
          return (
            <button
              key={s.place_id}
              type="button"
              data-testid={`${testId}-option-${i}`}
              onMouseEnter={() => setHighlight(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(s)}
              className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors border-b border-brand-border-warm/50 last:border-0 ${
                active ? "bg-brand-light-green" : "bg-white hover:bg-brand-light-green/60"
              }`}
            >
              {isPlace ? (
                <span
                  className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white mt-0.5"
                  style={{ backgroundColor: placeCat?.color || "#4A7C59" }}
                  aria-hidden
                >
                  {Icon ? <Icon className="w-3.5 h-3.5" strokeWidth={2.6} /> : <PawPrint className="w-3.5 h-3.5" />}
                </span>
              ) : (
                <MapPin className="w-4 h-4 mt-0.5 text-brand-forest shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-brand-ink truncate flex items-center gap-1.5">
                  {s.main_text}
                  {isPlace && s.rating ? (
                    <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-brand-ink/70">
                      <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                      {Number(s.rating).toFixed(1)}
                    </span>
                  ) : null}
                </div>
                {s.secondary_text && (
                  <div className="text-xs text-brand-ink/60 truncate">
                    {isPlace ? `${placeCat?.label || "Place"} · ` : ""}{s.secondary_text}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    ) : null;

  return (
    <div className={`flex flex-col sm:flex-row gap-3 ${className}`}>
      <div className="relative flex-1" ref={inputWrapRef}>
        <MapPin className={`absolute left-4 top-1/2 -translate-y-1/2 ${size === "lg" ? "w-5 h-5" : "w-4 h-4"} text-brand-ink/40 pointer-events-none`} />
        <input
          ref={inputRef}
          data-testid={testId}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          autoFocus={autoFocus}
          autoComplete="off"
          spellCheck={false}
          className={inputClasses}
        />
        {!showButton && (
          <button
            type="button"
            onClick={handleSubmit}
            data-testid={`${testId}-submit`}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-brand-forest text-white rounded-full px-5 py-2 text-sm font-semibold hover:bg-brand-forest-dark transition-colors"
          >
            Search
          </button>
        )}
        {typeof document !== "undefined" && createPortal(dropdown, document.body)}
      </div>

      {showButton && (
        <button
          type="button"
          onClick={handleSubmit}
          data-testid={`${testId}-submit`}
          className={`bg-brand-forest text-white font-semibold rounded-full ${
            size === "lg" ? "px-7 py-4" : "px-5 py-3"
          } hover:bg-brand-forest-dark transition-colors active:scale-95 shadow-warm flex items-center justify-center gap-2`}
        >
          <SearchIcon className="w-4 h-4" />
          {buttonLabel}
        </button>
      )}
    </div>
  );
};

export default LocationAutocomplete;
